import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";
import { ALL_LOCATIONS } from "@/data/bookingOptions";
import { trackEvent, attachCampaign } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COACHING_TYPES = [
  { v: "choreography",  label: "Choreography coaching" },
  { v: "wedding",       label: "Wedding dance preparation" },
  { v: "confidence",    label: "Confidence building" },
  { v: "battles",       label: "Dance battles training" },
  { v: "freestyle",     label: "Freestyle development" },
  { v: "auditions",     label: "Audition preparation" },
  { v: "performance",   label: "Performance coaching" },
  { v: "online-live",   label: "Online live coaching" },
  { v: "face-to-face",  label: "Face-to-face coaching" },
  { v: "other",         label: "Other / mixed" },
];

const blankForm = {
  name: "",
  email: "",
  phone: "",
  age: "",
  guardian_name: "",
  coaching_type: "",
  delivery: "either",
  preferred_location: "",
  preferred_days: "",
  goal: "",
  experience: "",
  num_people: "1",
  budget: "",
  consent: false,
};

const Field = ({ label, children, testId }) => (
  <label data-testid={testId} className="block">
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 font-bold">{label}</div>
    {children}
  </label>
);

export default function PrivateCoachingPage() {
  usePageMeta(
    "Private 1-to-1 Dance Coaching",
    "Book personalised dance coaching with USDD Academy teachers — choreography, wedding dance, battles, auditions, performance training. Online live or face-to-face."
  );
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.coaching_type) {
      toast.error("Please fill name, email, phone and pick a coaching type.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent to be contacted about your enquiry.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/enquiries/private-coaching`, attachCampaign(form));
      trackEvent("private_coaching_quote", { coaching_type: form.coaching_type, delivery: form.delivery });
      setDone(true);
      toast.success("Enquiry submitted — we'll respond with a quote.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="USDD Academy · Private 1-to-1"
          title="Bespoke."
          highlight="One-to-one coaching."
          description="Book personalised dance coaching with USDD Academy teachers — choreography, weddings, battles, auditions, performance & freestyle. Delivered online live or face-to-face depending on your goals."
          ctaPrimary={{ label: "Request a Quote", to: "#enquiry-form" }}
          ctaSecondary={{ label: "Adult Training", to: "/training" }}
          bg={SITE.classImg7}
        />

        <section id="enquiry-form" className="relative py-16 sm:py-20 bg-black overflow-hidden">
          <div className="absolute -top-20 right-1/4 w-[480px] h-[480px] rounded-full bg-brand-yellow/10 blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-20 left-1/4 w-[400px] h-[400px] rounded-full bg-brand-purpleLight/15 blur-[110px] pointer-events-none" />

          <div className="relative max-w-3xl mx-auto px-6 sm:px-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-brand-yellow/40 bg-gradient-to-br from-brand-purpleDeep/40 via-zinc-950 to-black p-10 text-center"
                data-testid="private-coaching-thanks"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 mb-5">
                  <Sparkles size={22} className="text-brand-yellow" />
                </div>
                <h3 className="font-display text-3xl sm:text-4xl text-white tracking-wide">Enquiry received.</h3>
                <p className="mt-3 text-sm text-zinc-300 leading-relaxed max-w-xl mx-auto">
                  Thanks — a member of the USDD team will be in touch within 2 working days with a tailored quote and next steps.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
                >
                  Back to home <ArrowRight size={14} />
                </Link>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                onSubmit={onSubmit}
                data-testid="private-coaching-form"
                className="relative overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/30 via-zinc-950 to-black p-7 sm:p-10 space-y-5"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow font-black mb-2">Step 1 of 1 · Enquiry form</div>
                  <h2 className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-[1]">
                    Request a Quote.
                  </h2>
                  <p className="mt-3 text-sm text-zinc-400">
                    Private sessions aren't bookable instantly — share your goal and we'll come back with availability and pricing.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Your name *" testId="pc-name">
                    <input type="text" required value={form.name} onChange={update("name")} className="usdd-input" placeholder="Full name" data-testid="pc-name-input" />
                  </Field>
                  <Field label="Email *" testId="pc-email">
                    <input type="email" required value={form.email} onChange={update("email")} className="usdd-input" placeholder="you@example.com" data-testid="pc-email-input" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Phone / WhatsApp *" testId="pc-phone">
                    <input type="tel" required value={form.phone} onChange={update("phone")} className="usdd-input" placeholder="07XXX XXXXXX" data-testid="pc-phone-input" />
                  </Field>
                  <Field label="Age (if under 18)" testId="pc-age">
                    <input type="text" value={form.age} onChange={update("age")} className="usdd-input" placeholder="e.g. 15" data-testid="pc-age-input" />
                  </Field>
                </div>

                {form.age && Number(form.age) < 18 && (
                  <Field label="Parent / Guardian name *" testId="pc-guardian">
                    <input type="text" required value={form.guardian_name} onChange={update("guardian_name")} className="usdd-input" placeholder="e.g. Sam Johnson" data-testid="pc-guardian-input" />
                  </Field>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Type of coaching *" testId="pc-type">
                    <select required value={form.coaching_type} onChange={update("coaching_type")} className="usdd-input cursor-pointer" data-testid="pc-type-input">
                      <option value="">— Select —</option>
                      {COACHING_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Delivery *" testId="pc-delivery">
                    <select required value={form.delivery} onChange={update("delivery")} className="usdd-input cursor-pointer" data-testid="pc-delivery-input">
                      <option value="online">Online live</option>
                      <option value="in-person">Face-to-face</option>
                      <option value="either">Either / flexible</option>
                    </select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Preferred location" testId="pc-location">
                    <select value={form.preferred_location} onChange={update("preferred_location")} className="usdd-input cursor-pointer" data-testid="pc-location-input">
                      <option value="">Any / online</option>
                      {ALL_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Preferred days / times" testId="pc-days">
                    <input type="text" value={form.preferred_days} onChange={update("preferred_days")} className="usdd-input" placeholder="e.g. Sat mornings · weekday evenings" data-testid="pc-days-input" />
                  </Field>
                </div>

                <Field label="Goal / reason for session *" testId="pc-goal">
                  <textarea rows={3} required value={form.goal} onChange={update("goal")} className="usdd-input resize-none" placeholder="e.g. Wedding first dance in July · prep for an audition · build confidence for battles." data-testid="pc-goal-input" />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Experience level" testId="pc-experience">
                    <select value={form.experience} onChange={update("experience")} className="usdd-input cursor-pointer" data-testid="pc-experience-input">
                      <option value="">Choose</option>
                      <option value="Beginner">Beginner</option>
                      <option value="Improver">Improver</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Pro / industry">Pro / industry</option>
                    </select>
                  </Field>
                  <Field label="Number of people" testId="pc-num">
                    <input type="text" value={form.num_people} onChange={update("num_people")} className="usdd-input" placeholder="e.g. 1, 2, group of 4" data-testid="pc-num-input" />
                  </Field>
                  <Field label="Budget (optional)" testId="pc-budget">
                    <input type="text" value={form.budget} onChange={update("budget")} className="usdd-input" placeholder="e.g. £150-£300" data-testid="pc-budget-input" />
                  </Field>
                </div>

                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="pc-consent">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 h-4 w-4 rounded border-white/30 bg-white/10 text-brand-yellow focus:ring-brand-yellow" data-testid="pc-consent-input" />
                  <span className="text-xs text-zinc-300 leading-relaxed">
                    <span className="font-bold text-white">I consent to be contacted</span> about my private coaching enquiry. See our Privacy &amp; GDPR Policy.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="private-coaching-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  Request a Quote <ArrowRight size={16} />
                </button>

                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5 justify-center">
                  <ShieldCheck size={12} /> Your details stay private — info@usddnetworks.co.uk
                </div>
              </motion.form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
